import { faCheck, faEdit, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useRef, useState } from "react";
import { GET_CLIENTS_END_POINT, PIPE_LINE_UPDATE_CDD_PIPELINE, PIPELINE_ROLES_BY_CLIENT } from "../../../../config/config";
import { api, getToken } from "../../../../services/apis/auth-api/apiUrl";

interface PipelineData {
  pipeline_id : number;
  cdd_name: string;
  client_id : number;
  role : string;
  revenue: string;
  processed_date: string; 
  status: number;
  notes: string;
  projected_start_date: string; 
  client_name: string;
  career_level: string;
  cdd_num: string;
  add_candidates_or_switch_roles: string;
}

interface ViewModalProps {
    access : number;
    pipeline : PipelineData;
    onClose : ()=>void;
}

interface clientsProps {
    id: number;
    client_name : string;
}

interface roleProps {
    id : number;
    position_title : string;
    career_level : string;
}

const ViewCddPipeline : React.FC<ViewModalProps> = ({access, pipeline, onClose}) => {
    const [pipelineData, setPipelineData] = useState<PipelineData>(pipeline);
    const [clients, setClients] = useState<clientsProps[] | null>(null);
    const [roles, setRoles] = useState<roleProps[] | null>(null);
    const [editting, setEditing] = useState<boolean>(true);
    const divFloatCard = useRef<HTMLDivElement | null>(null);
    const _divFloatCard = useRef<HTMLDivElement | null>(null);
    const [clientSearch, setClientSearch] = useState<string>('');
    const [roleSearch, setRoleSearch] = useState<string>('');
    const [showClientSearchCard, setShowClientSearchCard] = useState<boolean>(false);
    const [showRolesSearchCard, setShowRolesSearchCard] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [preservedOriginalPipeline, setOriginalPipeline] = useState<PipelineData>(pipeline);
    const [cddSaved, setCddSaved] = useState<boolean | null>(null);
    const [savedStatus, setSavedStatus] = useState<string>('');
    const [fieldsValid, setFieldValid] = useState<boolean | null>(null);
    const clientTextBox = useRef<HTMLInputElement | null>(null);
    const roleTextBox = useRef<HTMLInputElement | null>(null);

    const handleEdit = () => {
        setOriginalPipeline(pipelineData);
        setEditing(true);
    }

    const handleCancelEdit = () => {
        setPipelineData(preservedOriginalPipeline);
        setEditing(false);
    }

    const handleClientId = (id : number) => {
        setPipelineData({...pipelineData, client_id: id})
        setShowClientSearchCard(false);
    }

    const handleRoles = (id : number) => {
        setPipelineData({...pipelineData, role : ''});
        setShowRolesSearchCard(false);
    }

    const handleClickOutside = (event: MouseEvent) => {
        if (
            !(_divFloatCard.current?.contains(event.target as Node)) &&
            !(divFloatCard.current?.contains(event.target as Node))
        ) {
         setShowClientSearchCard(false);
         setShowRolesSearchCard(false);
        }
    };

    const handleSubmitUpdates = async()=>{
        let requiredFieldsHasData = true;
                if(
                    pipelineData.processed_date === '' ||
                    pipelineData.cdd_name === '' ||
                    pipelineData.client_id === 0 ||
                    pipelineData.role === '' ||
                    pipelineData.career_level === '' ||
                    pipelineData.projected_start_date === '' ||
                    !pipelineData.projected_start_date
                ){
                    requiredFieldsHasData = false;
                }
                
                if(!requiredFieldsHasData){
                    setFieldValid(false);
                    setCddSaved(false);
                    setSavedStatus('Please complete all the required fields! (Red border)');
                    return false;
                }
                setLoading(true);
                try{
                    await api.put(
                        PIPE_LINE_UPDATE_CDD_PIPELINE, pipelineData, {
                            headers : {'Authorization' : 'Bearer '+getToken()}
                        }
                    );
                    setCddSaved(true);
                    setSavedStatus('Pipeline updated!');
                    setLoading(false);
                    setOriginalPipeline(pipelineData);
                    setEditing(false);
                    onClose();
                }catch(e){
                    setCddSaved(false);
                    setSavedStatus('Failed to update pipeline!');
                    setLoading(false);
                }
    }

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(()=>{
        const getClients = async() => {
            try{
                const clients = await api.get(
                    GET_CLIENTS_END_POINT,{
                        headers : {'Authorization' : 'Bearer '+getToken()}
                    }
                );
                const data = clients.data as clientsProps[];
                setClients(data);
            }catch(e){}
        }
        if(!clients) getClients();
    },[clients]);

    useEffect(()=>{
        const setRolesArr = async() => {
            try{
                const roles = await api.get(
                    PIPELINE_ROLES_BY_CLIENT+pipelineData.client_id,{
                        headers : {'Authorization' : 'Bearer '+getToken()}
                    }
                );
                const data = roles.data as roleProps[];
                setRoles(data);
                setLoading(false);
            }catch(e){}
        }
        if(pipelineData.client_id !== 0) setRolesArr();
    },[pipelineData.client_id])

    useEffect(()=>{
        if(showClientSearchCard) clientTextBox.current?.focus();
        if(showRolesSearchCard) roleTextBox.current?.focus();
    },[showClientSearchCard, showRolesSearchCard]);

    return(
        <div className="min-w-[28rem] max-w-[48rem] relative">
            <div className={loading ? 'modal-loader' : 'hidden'}>
                <FontAwesomeIcon className="animate-spin" icon={faSpinner} />
            </div>
            <div className="text-center uppercase font-medium text-gray-600 text-xl">
                
                {editting ? ( 
                    <h1>
                        <FontAwesomeIcon icon={faEdit} /> &nbsp;
                        Editing CDD Pipeline
                    </h1>
                   
                ) : 'Candidate Pipeline'}
            </div>
            <form>
                <div className={cddSaved ? 'text-green-600' : 'text-red-500'}>
                    {savedStatus}
                </div>
                <div className="flex gap-4">
                    <div className="w-1/2">
                        <div className='input-container'>
                            <label>Processed Date: </label>
                            <input 
                                type="date"
                                readOnly={!editting}
                                value={pipelineData.processed_date}
                                onChange={(e)=>setPipelineData({...pipelineData, processed_date: e.currentTarget.value})}
                                className={`text-box cursor-pointer ${fieldsValid === false && !pipelineData.processed_date ? 'border-2 border-red-400' : ''}`}
                            />
                        </div>
                        <div className='input-container'>
                            <label>Candidate Name: </label>
                            <input 
                                type="text"
                                readOnly={!editting}
                                value={pipelineData.cdd_name}
                                onChange={(e)=>setPipelineData({...pipelineData, cdd_name : e.currentTarget.value})}
                                className={`text-box ${fieldsValid === false && !pipelineData.cdd_name ? 'border-2 border-red-400' : ''}`}
                            />
                        </div>
                        <div className='input-container relative'>
                            <label>Client:</label>
                            <input 
                                type="text" 
                                readOnly
                                onClick={()=>setShowClientSearchCard(!editting ? false : showClientSearchCard ? false : true)} 
                                value={clients?.find(client => client.id === pipelineData.client_id)?.client_name || ''}
                                className={`text-box ${fieldsValid === false && !pipelineData.client_id ? 'border-2 border-red-400' : ''}`} 
                            />
                            <div ref={divFloatCard} className={!showClientSearchCard ? "hidden" : "absolute bg-white p-2 shadow-xl border-2 w-full mt-0.5 h-52 max-h-52 z-50"}>
                                <div className="flex flex-col">
                                    <label className="text-sm">Search client:</label>
                                    <input type="text" ref={clientTextBox} onChange={(e)=>setClientSearch(e.currentTarget.value)} value={clientSearch} className="outline-none px-2 border border-gray-400" />
                                </div>
                                <div className="mb-2">
                                    <small>Clients:</small>
                                </div>
                                <div className="flex flex-col max-h-28 overflow-y-auto">
                                    {clientSearch !== '' ? clients
                                        ?.filter(client => client.client_name.toLowerCase().includes(clientSearch.toLowerCase()))
                                        .map(client => (
                                            <div key={client.id} onClick={()=>handleClientId(client.id)} className="hover:bg-gray-200 py-1 px-1 cursor-pointer">
                                                {client.client_name}
                                            </div>
                                    )) : (<div>No client found.</div>)}
                                </div>
                            </div>
                        </div>
                        <div className='input-container relative'>
                            <label>Role/Position title: </label>
                            <input 
                                type="text"
                                readOnly
                                value={pipelineData.role}
                                onClick={()=>setShowRolesSearchCard(!editting ? false : pipelineData.client_id === 0 ? false : showRolesSearchCard ? false : true)} 
                                className={`text-box ${fieldsValid === false && !pipelineData.role ? 'border-2 border-red-400' : ''}`}
                            />
                            <div ref={_divFloatCard} className={!showRolesSearchCard ? "hidden" : "absolute bg-white p-2 shadow-xl border-2 w-full mt-0.5 h-52 max-h-52 z-50"}>
                                <div className="flex flex-col">
                                    <label className="text-sm">Type role:</label>
                                    <input type="text" ref={roleTextBox} onChange={(e)=>setPipelineData({...pipelineData, role : e.currentTarget.value})} className="outline-none px-2 border border-gray-400" />
                                </div>
                                <div className="mb-2">
                                    <small>Roles:</small>
                                </div>
                                <div className="flex flex-col max-h-28 overflow-y-auto">
                                    {/* {roleSearch !== '' ? roles
                                        ?.filter(role => role.position_title.toLowerCase().includes(roleSearch.toLowerCase()))
                                        .map(role => (
                                            <div key={role.id} onClick={()=>handleRoles(role.id)} className="hover:bg-gray-200 py-1 px-1 cursor-pointer">
                                                {role.position_title}
                                            </div>
                                    )) : (<div>No role found.</div>)} */}
                                </div>
                            </div>
                        </div>
                        <div className='input-container flex gap-2'>
                            <div>
                                <label>Career Level:</label>
                                <input 
                                    type="text"
                                    value={pipelineData.career_level}
                                    onChange={(e)=>setPipelineData({...pipelineData, career_level : e.currentTarget.value})}
                                    className={`text-box ${fieldsValid === false && pipelineData.career_level === '' ? 'border-2 border-red-400' : ''}`}
                                />
                            </div>
                            <div>
                                <label>Projected Start Date: </label>
                                <input 
                                    type="date" 
                                    readOnly={!editting} 
                                    onChange={(e)=>setPipelineData({...pipelineData, projected_start_date : e.currentTarget.value})} 
                                    value={pipelineData.projected_start_date} 
                                    className={`text-box ${fieldsValid === false && (pipelineData.projected_start_date === '' || !pipelineData.projected_start_date) ? 'border-2 border-red-400' : ''}`} />
                            </div>
                        </div>
                        <div className='input-container'>
                            <label>Status:</label>
                            <select disabled={!editting} onChange={(e)=>setPipelineData({...pipelineData, status : parseInt(e.currentTarget.value)})} className="text-box">
                                <option value="1" selected={pipelineData.status === 1}>On Initial Stage</option>
                                <option value="2" selected={pipelineData.status === 2}>On Mid Stage</option>
                                <option value="3" selected={pipelineData.status === 3}>On Final Stage</option>
                                <option value="4" selected={pipelineData.status === 4}>For Job Offer</option>
                                <option value="5" selected={pipelineData.status === 5}>Offer Accepted</option>
                                <option value="6" selected={pipelineData.status === 6}>Onboarded</option>
                                <option value="7" selected={pipelineData.status === 7}>Withdraw</option>
                                <option value="8" selected={pipelineData.status === 8}>Reneged</option>
                                <option value="9" selected={pipelineData.status === 9}>Source Tagging</option>
                                <option value="10" selected={pipelineData.status === 10}>Closed Role</option>
                                <option value="11" selected={pipelineData.status === 11}>Offer Rejected</option>
                                <option value="0" selected={pipelineData.status === 0}>Failed</option>
                            </select>
                        </div>
                    </div>
                    <div className="w-1/2">
                        <div className='input-container'>
                            <label>Status Note (Optional):</label>
                            <textarea readOnly={!editting} onChange={(e)=>setPipelineData({...pipelineData, notes : e.currentTarget.value})} value={pipelineData.notes} className='text-box min-h-24' />
                        </div>
                        <div className='input-container'>
                            <label>Revenue: <i className={editting && access === 1 ? "text-rose-500" : "hidden"}>You cannot update this field.</i></label>
                            <input 
                                type="number"
                                step="any"
                                readOnly={!(editting && access === 0)}
                                value={pipelineData.revenue}
                                onChange={(e)=>setPipelineData({...pipelineData, revenue : e.currentTarget.value})}
                                className={`text-box`}
                            />
                        </div>
                        <div className='input-container'>
                            <label>HOW MANY CANDIDATES DO YOU HAVE IN THE PIPELINE FOR THIS ROLE? (Optional)</label>
                            <input 
                                type="text" 
                                readOnly={!editting}
                                value={pipelineData.cdd_num}
                                onChange={(e)=>setPipelineData({...pipelineData, cdd_num : e.currentTarget.value})}
                                className={`text-box ${fieldsValid === false && !pipelineData.cdd_num ? 'border-2 border-red-400' : ''}`}
                            />
                        </div>
                        <div className='input-container'>
                            <label>WILL YOU BE ADDING MORE CANDIDATES OR MOVING ON TO OTHER ROLES? (Optional)</label>
                            <textarea readOnly={!editting} onChange={(e)=>setPipelineData({...pipelineData, add_candidates_or_switch_roles : e.currentTarget.value})} value={pipelineData.add_candidates_or_switch_roles} className='text-box h-24' />
                        </div>
                    </div>
                </div>

                <div className='input-container flex'>
                    <div className="ms-auto flex gap-2 items-center">
                        {editting ? (
                            <>
                            <div>
                                <button type="button" onClick={handleSubmitUpdates} className='button button-vcc-green'>
                                    <FontAwesomeIcon icon={faCheck} />&nbsp;
                                    Update
                                </button>
                            </div>
                            <div className="hidden">
                                <button type="button" onClick={handleCancelEdit} className='button button-red'>
                                    <FontAwesomeIcon icon={faTimes} />&nbsp;
                                    Cancel
                                </button>
                            </div>
                        </>
                        ): (
                            <div>
                                <button type="button" onClick={handleEdit}  className='button button-vcc-green'>
                                    <FontAwesomeIcon icon={faEdit} />&nbsp;
                                    Edit
                                </button>
                            </div>
                        )}
                        
                    </div>
                </div>
            </form>
        </div>
    );
}

export default ViewCddPipeline;